import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import { buyerLandingPageMap, resolveBuyerProducts } from '@/data/buyerLandingPages';
import { getProductSideImage } from '@/lib/productImages';
import NotFound from '@/pages/NotFound';

const money = new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS', maximumFractionDigits: 2,
});
const focusClass = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-700 focus-visible:ring-offset-4';
const primaryClass = `inline-flex min-h-[48px] items-center justify-center gap-3 rounded-full bg-purple-700 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-purple-800 ${focusClass}`;
const textLinkClass = `inline-flex min-h-[44px] items-center gap-2 rounded-sm font-semibold underline decoration-purple-300 underline-offset-4 transition-colors hover:text-purple-800 ${focusClass}`;

function quantityLabel(tier) {
  return tier.max === Infinity ? `${tier.min}+` : tier.min === tier.max ? `${tier.min}` : `${tier.min}–${tier.max}`;
}

function ProductChoice({ selection, personal, reducedMotion }) {
  const { product, label, reason, tiers } = selection;
  const displayTier = personal
    ? tiers.find((tier) => tier.min <= 1 && tier.max >= 1)
    : tiers.reduce((lowest, tier) => tier.price < lowest.price ? tier : lowest, tiers[0]);
  const image = getProductSideImage(product, 'front', [product.colors[0]]);

  return (
    <article className="group flex min-w-0 flex-col border-b border-slate-200 pb-7">
      <Link to={`/product/${product.sku}`} className={`mb-5 block overflow-hidden bg-slate-50 ${focusClass}`} aria-label={`לבחירת ${label}, צבעים ומידות`}>
        <img
          src={image}
          alt={`${label} מתוך הקטלוג, לפני מיתוג`}
          width="500"
          height="500"
          loading="lazy"
          className={`mx-auto aspect-square w-full max-w-[350px] object-contain p-5 mix-blend-multiply ${reducedMotion ? '' : 'transition-transform duration-300 group-hover:scale-[1.04]'}`}
        />
      </Link>
      <h3 className="text-2xl font-semibold text-slate-950">{label}</h3>
      <p className="mt-2 flex-1 text-base leading-7 text-slate-600">{reason}</p>
      <div className="mt-5">
        {displayTier ? (
          <>
            <p className="text-xl font-semibold text-slate-950">
              {personal ? 'מחיר הבגד ליחידה אחת: ' : 'מחיר הבגד החל מ־'}
              <bdi>{money.format(displayTier.price)}</bdi>
            </p>
            {!personal && <p className="mt-1 text-sm text-slate-600">ליחידה, בכמות <bdi dir="ltr">{quantityLabel(displayTier)}</bdi> יחידות</p>}
          </>
        ) : <p className="font-semibold">מחיר לפי הכמות בדף המוצר</p>}
        <p className="mt-1 text-sm leading-6 text-slate-600">כולל מע״מ, לפני מיתוג ומשלוח.</p>
        <details className="mt-3">
          <summary className={`min-h-[44px] cursor-pointer rounded-sm py-2 text-sm font-semibold text-purple-800 ${focusClass}`}>
            כל מחירי הבגד לפי כמות
          </summary>
          <table className="mt-2 w-full text-sm">
            <caption className="sr-only">מחיר בגד {label} ליחידה, כולל מע״מ ולפני מיתוג ומשלוח</caption>
            <thead>
              <tr className="border-b border-slate-200 text-slate-600">
                <th scope="col" className="py-2 text-start font-medium">כמות יחידות</th>
                <th scope="col" className="py-2 text-end font-medium">מחיר בגד ליחידה</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={`${tier.min}-${tier.max}`} className="border-b border-slate-100">
                  <th scope="row" className="py-2 text-start font-normal"><bdi dir="ltr">{quantityLabel(tier)}</bdi></th>
                  <td className="py-2 text-end"><bdi>{money.format(tier.price)}</bdi></td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>
      <Link to={`/product/${product.sku}`} className={`${primaryClass} mt-5 w-full`}>
        לבחירת {label} ולעיצוב
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
      </Link>
    </article>
  );
}

export default function BuyerLandingPage({ landingKey }) {
  const reducedMotion = useReducedMotion();
  const landing = buyerLandingPageMap[landingKey];
  if (!landing) return <NotFound />;

  const selections = resolveBuyerProducts(landing);
  const heroProduct = selections.find(({ sku }) => sku === landing.heroSku)?.product;
  const heroImage = getProductSideImage(heroProduct, 'front', [landing.heroColor]);
  const reveal = reducedMotion ? {} : {
    initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.1 }, transition: { duration: 0.4 },
  };

  return (
    <div className="bg-white text-start text-slate-950" dir="rtl">
      <section className="overflow-hidden bg-[#f4f1f7]" aria-labelledby="buyer-title">
        <div className="mx-auto grid max-w-7xl items-center gap-4 px-5 py-9 sm:px-8 sm:py-12 md:min-h-[560px] md:grid-cols-2 md:gap-10 lg:py-16">
          <motion.div initial={reducedMotion ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-bold tracking-[0.16em] text-purple-800"><bdi dir="ltr">PRINTEAM</bdi></p>
            <p className="mt-4 text-sm font-medium text-slate-600">{landing.eyebrow}</p>
            <h1 id="buyer-title" className="mt-3 max-w-xl text-4xl font-semibold leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">{landing.title}</h1>
            <p className="mt-5 max-w-lg text-lg leading-8 text-slate-700">{landing.description}</p>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              <a href="#buyer-products" className={primaryClass}>{landing.primaryLabel}<ArrowLeft className="h-4 w-4" aria-hidden="true" /></a>
              <Link to="/contact" className={textLinkClass}>לעזרה ולוואטסאפ</Link>
            </div>
          </motion.div>
          <motion.figure className="m-0 min-w-0" initial={reducedMotion ? false : { opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <img src={heroImage} alt={landing.heroAlt} width="600" height="600" fetchpriority="high" loading="eager" className="mx-auto h-[210px] w-full object-contain mix-blend-multiply sm:h-[320px] md:h-[440px]" />
            <figcaption className="mt-2 text-center text-xs leading-5 text-slate-600">הבגד לפני מיתוג. צבעים ומידות בוחרים בדף המוצר.</figcaption>
          </motion.figure>
        </div>
      </section>

      <section id="buyer-products" className="scroll-mt-28 px-5 py-14 sm:px-8 lg:py-20" aria-labelledby="buyer-products-title">
        <div className="mx-auto max-w-7xl">
          <motion.div {...reveal} className="mb-9 max-w-2xl">
            <h2 id="buyer-products-title" className="text-3xl font-semibold tracking-tight sm:text-4xl">{landing.selectionTitle}</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">{landing.selectionIntro}</p>
          </motion.div>
          <div className={`grid gap-x-7 gap-y-12 sm:grid-cols-2 ${selections.length === 4 ? 'lg:grid-cols-4' : selections.length === 3 ? 'lg:grid-cols-3' : 'mx-auto max-w-4xl'}`}>
            {selections.map((selection) => <ProductChoice key={selection.sku} selection={selection} personal={landing.key === 'personal-design'} reducedMotion={reducedMotion} />)}
          </div>
          <p className="mt-7 max-w-3xl text-sm leading-7 text-slate-600">
            המחירים כאן הם לבגד בלבד. בדף המוצר בוחרים צבעים ומידות, והמחיר מתעדכן לפי הכמות ואפשרויות המיתוג שנבחרו. עלות המשלוח מוצגת בנפרד בהזמנה.
          </p>
          <nav aria-label="מוצרים נוספים להזמנה" className="mt-4 flex flex-wrap gap-x-7 gap-y-2">
            {landing.relatedLinks.map((link) => <Link key={link.to} to={link.to} className={textLinkClass}>{link.label}<ArrowLeft className="h-4 w-4" aria-hidden="true" /></Link>)}
          </nav>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-slate-50 px-5 py-14 sm:px-8 lg:py-20" aria-labelledby="buyer-preparation-title">
        <div className="mx-auto max-w-7xl">
          <h2 id="buyer-preparation-title" className="text-3xl font-semibold sm:text-4xl">{landing.preparationTitle}</h2>
          <div className="mt-9 grid gap-8 md:grid-cols-3">
            {landing.preparation.map((item, index) => (
              <motion.div key={item.title} {...reveal}>
                <span className="text-sm font-semibold text-purple-700" aria-hidden="true"><bdi dir="ltr">0{index + 1}</bdi></span>
                <h3 className="mt-3 text-xl font-semibold">{item.title}</h3>
                <p className="mt-3 max-w-sm text-base leading-7 text-slate-600">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-14 sm:px-8 lg:py-20" aria-labelledby="buyer-process-title">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-2 lg:gap-20">
          <div>
            <h2 id="buyer-process-title" className="text-3xl font-semibold sm:text-4xl">מהבחירה שלכם, עד הייצור.</h2>
            <ol className="mt-8 space-y-6">
              {[
                ['בוחרים ומגדירים', 'בוחרים מוצר, צבעים, מידות וכמויות, ומגדירים את אזורי המיתוג בדף המוצר.'],
                ['מעלים עיצוב ובודקים מחיר', 'ממקמים את הקובץ, בודקים את פרטי המוצר והמחיר, וממשיכים לעגלה ולתשלום.'],
                ['מאשרים הדמיה לפני ייצור', 'בהזמנה עם מיתוג, מאשרים את ההדמיה לפני שההזמנה עוברת לייצור.'],
              ].map(([title, body], index) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-800" aria-hidden="true">{index + 1}</span>
                  <div><h3 className="text-lg font-semibold">{title}</h3><p className="mt-1 text-base leading-7 text-slate-600">{body}</p></div>
                </li>
              ))}
            </ol>
          </div>
          <div className="border-t border-slate-200 pt-8 lg:border-t-0 lg:pt-0">
            <h3 className="text-xl font-semibold">מחפשים דוגמאות או הסבר על המיתוג?</h3>
            <p className="mt-3 text-base leading-7 text-slate-600">{landing.worksText}</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/works" className={textLinkClass}>לעבודות שלנו<ArrowLeft className="h-4 w-4" aria-hidden="true" /></Link>
              <Link to="/methods-of-branding" className={textLinkClass}>לשיטות המיתוג</Link>
            </div>
            <div className="mt-8 border-t border-slate-200 pt-7">
              <h3 className="text-xl font-semibold">מתכננים לפי תאריך?</h3>
              <p className="mt-3 text-base leading-7 text-slate-600">זמן האספקה המשוער הוא 5–10 ימי עבודה ממועד אישור ההזמנה והגשת הקבצים. הזמנים עשויים להשתנות לפי עומס, חומרים, ייצור ומשלוח. למועד קבוע, בדקו איתנו לפני ההזמנה.</p>
              <Link to="/faq" className={`${textLinkClass} mt-3`}>לשאלות על ההזמנה והאספקה</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-purple-50 px-5 py-12 sm:px-8 lg:py-16" aria-labelledby="buyer-final-title">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-7 lg:flex-row lg:items-center">
          <h2 id="buyer-final-title" className="max-w-xl text-3xl font-semibold leading-tight sm:text-4xl">{landing.finalTitle}</h2>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <a href="#buyer-products" className={primaryClass}>{landing.primaryLabel}<ArrowLeft className="h-4 w-4" aria-hidden="true" /></a>
            <Link to="/contact" className={textLinkClass}>לעזרה ולוואטסאפ</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
